import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  Edit3,
  ExternalLink,
  Eye,
  Grid,
  List,
  Mail,
  Phone,
  RefreshCw,
  Search,
  Shield,
  Star,
  UserCheck,
  Users,
  XCircle
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { api } from '../../services/api';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import AdminPageBanner from '../../components/admin/AdminPageBanner';

const CEAVerificationManagement = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showVerificationTools, setShowVerificationTools] = useState(false);
  const [verificationTools, setVerificationTools] = useState(null);
  const [loadingTools, setLoadingTools] = useState(false);
  const [verificationForm, setVerificationForm] = useState({
    status: '',
    notes: ''
  });
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [ceaSearchTerm, setCeaSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchAgents();
    fetchStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/agents/cea-verification');
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to fetch agent data');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/agents/cea-stats');
      setStats(response.data || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set default stats if API fails
      setStats({
        totalAgents: agents.length,
        pendingVerification: agents.filter(a => a.ceaVerificationStatus === 'PENDING_MANUAL_VERIFICATION').length,
        verified: agents.filter(a => a.ceaVerificationStatus === 'VERIFIED').length,
        failed: agents.filter(a => a.ceaVerificationStatus === 'FAILED').length,
        expired: agents.filter(a => a.ceaVerificationStatus === 'EXPIRED').length,
        highRisk: agents.filter(a => a.ceaFraudRiskLevel === 'HIGH').length
      });
    }
  };

  const searchByCEACode = async () => {
    if (!ceaSearchTerm.trim()) {
      toast.error('Please enter a CEA registration number to search');
      return;
    }

    try {
      setIsSearching(true);
      console.log('Searching for CEA code:', ceaSearchTerm.trim());
      
      const response = await api.get(`/admin/agents/search-cea/${encodeURIComponent(ceaSearchTerm.trim())}`);
      
      console.log('Search response:', response);
      
      // Handle the response more safely
      const agents = response?.data || response || [];
      setAgents(Array.isArray(agents) ? agents : []);
      
      if (!agents || agents.length === 0) {
        toast.success('No agents found with that CEA registration number');
      } else {
        toast.success(`Found ${agents.length} agent(s) with CEA number: ${ceaSearchTerm}`);
      }
    } catch (error) {
      console.error('Error searching by CEA code:', error);
      console.error('Error details:', error.response || error.message);
      
      // Set empty array on error
      setAgents([]);
      
      if (error.response?.status === 404) {
        toast.error('Search endpoint not found. Please check if the backend server is running.');
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to search by CEA code: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setCeaSearchTerm('');
    fetchAgents(); // Reload all agents
  };

  const openVerificationModal = (agent) => {
    setSelectedAgent(agent);
    setVerificationForm({
      status: agent.ceaVerificationStatus || 'PENDING_MANUAL_VERIFICATION',
      notes: agent.ceaVerificationNotes || ''
    });
    setShowVerificationModal(true);
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAgent) return;

    // Check if agent has Gmail address for verification
    const gmailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/i;
    if (!gmailRegex.test(selectedAgent.email)) {
      toast.error('Verification failed: Agent must have a Gmail address to be verified');
      return;
    }

    try {
      await api.put(`/admin/agents/${selectedAgent._id}/cea-verification`, verificationForm);
      
      toast.success('CEA verification updated successfully');
      setShowVerificationModal(false);
      fetchAgents();
    } catch (error) {
      console.error('Error updating verification:', error);
      toast.error('Failed to update verification');
    }
  };

  const openVerificationTools = async (agent) => {
    setSelectedAgent(agent);
    setLoadingTools(true);
    setShowVerificationTools(true);
    
    try {
      const response = await api.get(`/admin/agents/${agent._id}/verification-tools`);
      setVerificationTools(response);
    } catch (error) {
      console.error('Error fetching verification tools:', error);
      toast.error('Failed to load verification tools');
      setVerificationTools(null);
    } finally {
      setLoadingTools(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'PENDING_MANUAL_VERIFICATION': {
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock,
        text: 'Pending Verification'
      },
      'VERIFIED': {
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle,
        text: 'Verified'
      },
      'FAILED': {
        color: 'bg-red-100 text-red-800',
        icon: XCircle,
        text: 'Failed'
      },
      'EXPIRED': {
        color: 'bg-orange-100 text-orange-800',
        icon: AlertTriangle,
        text: 'Expired'
      },
      'SUSPENDED': {
        color: 'bg-red-100 text-red-800',
        icon: XCircle,
        text: 'Suspended'
      }
    };

    const config = statusMap[status] || statusMap['PENDING_MANUAL_VERIFICATION'];
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <IconComponent size={12} className="mr-1" />
        {config.text}
      </span>
    );
  };

  const getFraudRiskBadge = (riskLevel, warnings = []) => {
    const riskMap = {
      'LOW': {
        color: 'bg-green-100 text-green-800',
        icon: Shield,
        text: 'Low Risk'
      },
      'MEDIUM': {
        color: 'bg-yellow-100 text-yellow-800',
        icon: AlertTriangle,
        text: 'Medium Risk'
      },
      'HIGH': {
        color: 'bg-red-100 text-red-800',
        icon: AlertTriangle,
        text: 'High Risk'
      }
    };

    const config = riskMap[riskLevel] || riskMap['LOW'];
    const IconComponent = config.icon;

    return (
      <div className="flex flex-col">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
          <IconComponent size={12} className="mr-1" />
          {config.text}
        </span>
        {warnings && warnings.length > 0 && (
          <span className="text-xs text-red-600 mt-1" title={warnings.join(', ')}>
            {warnings.length} warning{warnings.length > 1 ? 's' : ''}
          </span>
        )}
      </div>
    );
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (agent.ceaRegistrationNumber && agent.ceaRegistrationNumber.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filterStatus === 'ALL' || agent.ceaVerificationStatus === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const isExpiryNear = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expiry <= thirtyDaysFromNow;
  };

  // Agent Card Component
  const AgentCard = ({ agent }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 p-6"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
              {agent.fullName?.[0] || agent.email[0].toUpperCase()}
            </div>
            {agent.ceaVerificationStatus === 'VERIFIED' && (
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">
              {agent.fullName}
            </h3>
            <div className="flex items-center space-x-2 mt-1">
              {getStatusBadge(agent.ceaVerificationStatus)}
              {!agent.email?.toLowerCase().endsWith('@gmail.com') && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <AlertTriangle size={10} className="mr-1" />
                  Non-Gmail
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {getFraudRiskBadge(
            agent.ceaFraudRiskLevel || 'LOW',
            agent.ceaFraudWarnings || []
          )}
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center text-gray-600 text-sm">
          <Mail className="w-4 h-4 mr-2" />
          {agent.email}
        </div>
        {agent.phone && (
          <div className="flex items-center text-gray-600 text-sm">
            <Phone className="w-4 h-4 mr-2" />
            {agent.phone}
          </div>
        )}
        <div className="flex items-center text-gray-600 text-sm">
          <Shield className="w-4 h-4 mr-2" />
          CEA: {agent.ceaRegistrationNumber || 'Not provided'}
        </div>
        <div className="flex items-center text-gray-600 text-sm">
          <Star className="w-4 h-4 mr-2" />
          Agent Code: {agent.agentCode}
        </div>
      </div>

      {/* CEA Details */}
      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-gray-500 mb-1">Verification Date</p>
            <p className="text-sm font-medium">
              {agent.ceaVerificationDate
                ? new Date(agent.ceaVerificationDate).toLocaleDateString()
                : 'Not verified'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Expiry Date</p>
            {agent.ceaExpiryDate ? (
              <div>
                <p className={`text-sm font-medium ${isExpiryNear(agent.ceaExpiryDate) ? 'text-orange-600' : 'text-gray-900'}`}>
                  {new Date(agent.ceaExpiryDate).toLocaleDateString()}
                </p>
                {isExpiryNear(agent.ceaExpiryDate) && (
                  <p className="text-xs text-orange-500">Expires soon</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Not set</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
        <span>Joined: {new Date(agent.createdAt || Date.now()).toLocaleDateString()}</span>
        <span>
          Last Activity: {agent.lastLogin ? new Date(agent.lastLogin).toLocaleDateString() : 'Never'}
        </span>
      </div>

      {/* Action Buttons */}
      <div className="border-t border-gray-100 pt-4 mt-4">
        <div className="flex flex-wrap gap-2 justify-between items-center">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => openVerificationTools(agent)}
              className="flex items-center px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
              title="Open verification tools and checklist"
            >
              <Eye className="w-4 h-4 mr-1" />
              Verification Tools
            </button>

            <button
              onClick={() => openVerificationModal(agent)}
              className="flex items-center px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              Update Status
            </button>
          </div>

          <div className="flex gap-2">
            {agent.ceaVerificationStatus === 'PENDING_MANUAL_VERIFICATION' && (
              <button
                onClick={() => {
                  setSelectedAgent(agent);
                  setVerificationForm({
                    status: 'VERIFIED',
                    notes: 'Quick verified by admin'
                  });
                  handleVerificationSubmit(new Event('submit'));
                }}
                className="flex items-center px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
              >
                <UserCheck className="w-4 h-4 mr-1" />
                Quick Verify
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 -mt-8">
      <div className="pt-0"></div>
      <AdminPageBanner
        title="CEA Verification Management"
        subtitle="Manage and verify CEA (Council for Estate Agencies) registrations for property agents"
        icon={Shield}
        iconLabel="Admin Panel"
        actions={
          <>
            {stats.pendingVerification > 0 && (
              <button
                onClick={() => {
                  setFilterStatus('PENDING_MANUAL_VERIFICATION');
                  setSearchTerm('');
                }}
                className="flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-lg hover:bg-white/30 transition-colors"
              >
                <Clock className="w-4 h-4 mr-2" />
                View Pending ({stats.pendingVerification})
              </button>
            )}

            <button
              onClick={() => {
                fetchAgents();
                fetchStats();
              }}
              className="flex items-center px-4 py-2 bg-white text-orange-600 rounded-lg hover:bg-orange-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Agents</p>
                <p className="text-2xl font-bold text-blue-600">{stats.totalAgents || agents.length}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pendingVerification || agents.filter(a => a.ceaVerificationStatus === 'PENDING_MANUAL_VERIFICATION').length}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Verified</p>
                <p className="text-2xl font-bold text-green-600">{stats.verified || agents.filter(a => a.ceaVerificationStatus === 'VERIFIED').length}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Failed</p>
                <p className="text-2xl font-bold text-red-600">{stats.failed || agents.filter(a => a.ceaVerificationStatus === 'FAILED').length}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Expired</p>
                <p className="text-2xl font-bold text-orange-600">{stats.expired || agents.filter(a => a.ceaVerificationStatus === 'EXPIRED').length}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">High Risk</p>
                <p className="text-2xl font-bold text-red-600">{stats.highRisk || agents.filter(a => a.ceaFraudRiskLevel === 'HIGH').length}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="space-y-4">
            {/* CEA Code Search */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search by CEA Registration Number
                </label>
                <div className="relative">
                  <Shield size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Enter CEA registration number (e.g. R123456A)"
                    value={ceaSearchTerm}
                    onChange={(e) => setCeaSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchByCEACode()}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={searchByCEACode}
                  disabled={isSearching || !ceaSearchTerm.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSearching ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search size={16} />
                      Search CEA
                    </>
                  )}
                </button>
                <button
                  onClick={clearSearch}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2"
                >
                  <XCircle size={16} />
                  Show All
                </button>
              </div>
            </div>

            {/* General Search and Status Filter */}
            <div className="flex flex-col lg:flex-row gap-4 pt-4 border-t border-gray-200">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  General Search
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by name, email, or CEA number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status Filter
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[140px]"
                  >
                    <option value="ALL">All Status</option>
                    <option value="PENDING_MANUAL_VERIFICATION">Pending</option>
                    <option value="VERIFIED">Verified</option>
                    <option value="FAILED">Failed</option>
                    <option value="EXPIRED">Expired</option>
                    <option value="SUSPENDED">Suspended</option>
                  </select>
                </div>

                {/* View Toggle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    View Mode
                  </label>
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('cards')}
                      className={`p-3 ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      <Grid className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-3 ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      <List className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CEA Information Panel */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-2xl p-6 mb-8">
          <div className="flex items-start">
            <div className="p-3 bg-blue-100 rounded-xl mr-4">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-blue-900 mb-2">CEA Verification Process</h3>
              <p className="text-blue-800 mb-3">
                Verify agent registrations through the official CEA public register to ensure compliance and authenticity.
              </p>
              <a
                href="https://eservices.cea.gov.sg/aceas/public-register/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open CEA Public Register
              </a>
            </div>
          </div>
        </div>

        {/* Agent List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading agents...</p>
            </div>
          </div>
        ) : filteredAgents.length > 0 ? (
          viewMode === 'cards' ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredAgents.map((agent) => (
                <AgentCard key={agent._id} agent={agent} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Agent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CEA Registration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Security Risk
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Verification Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expiry
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAgents.map((agent) => (
                      <tr key={agent._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {agent.fullName}
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="text-sm text-gray-500">{agent.email}</div>
                              {!agent.email?.toLowerCase().endsWith('@gmail.com') && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <AlertTriangle size={10} className="mr-1" />
                                  Non-Gmail
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400">{agent.agentCode}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900">
                            {agent.ceaRegistrationNumber || 'Not provided'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(agent.ceaVerificationStatus)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getFraudRiskBadge(
                            agent.ceaFraudRiskLevel || 'LOW',
                            agent.ceaFraudWarnings || []
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {agent.ceaVerificationDate
                            ? new Date(agent.ceaVerificationDate).toLocaleDateString()
                            : 'Not verified'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {agent.ceaExpiryDate ? (
                            <div className={`text-sm ${isExpiryNear(agent.ceaExpiryDate) ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
                              {new Date(agent.ceaExpiryDate).toLocaleDateString()}
                              {isExpiryNear(agent.ceaExpiryDate) && (
                                <div className="text-xs text-orange-500">Expires soon</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">Not set</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => openVerificationTools(agent)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              title="Open verification tools and checklist"
                            >
                              <Eye size={14} className="mr-1" />
                              Tools
                            </button>
                            <button
                              onClick={() => openVerificationModal(agent)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <Edit3 size={14} className="mr-1" />
                              Verify
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ) : (
          <div className="text-center py-12">
            <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No Agents Found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || filterStatus !== 'ALL'
                ? 'No agents match your search criteria.'
                : 'No agents have been registered yet.'}
            </p>
            {(searchTerm || filterStatus !== 'ALL') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setCeaSearchTerm('');
                  setFilterStatus('ALL');
                }}
                className="text-blue-600 hover:text-blue-700"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Verification Modal */}
        {showVerificationModal && selectedAgent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full"
            >
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Verify CEA Registration
                </h3>
                
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">Agent: <span className="font-medium">{selectedAgent.fullName}</span></div>
                  <div className="text-sm text-gray-600">CEA Number: <span className="font-mono">{selectedAgent.ceaRegistrationNumber}</span></div>
                  <div className="text-sm text-gray-600">
                    Email: <span className="font-medium">{selectedAgent.email}</span>
                    {!/^[a-zA-Z0-9._%+-]+@gmail\.com$/i.test(selectedAgent.email) && (
                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <AlertTriangle size={12} className="mr-1" />
                        Non-Gmail
                      </span>
                    )}
                  </div>
                  <a 
                    href="https://eservices.cea.gov.sg/aceas/public-register/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm inline-flex items-center mt-1"
                  >
                    Verify on CEA Website <ExternalLink size={12} className="ml-1" />
                  </a>
                </div>

                {/* Gmail Requirement Warning */}
                {!/^[a-zA-Z0-9._%+-]+@gmail\.com$/i.test(selectedAgent.email) && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <AlertTriangle size={16} className="text-red-600 mr-2" />
                      <p className="text-sm text-red-800">
                        <strong>Gmail Required:</strong> This agent does not have a Gmail address. Only agents with Gmail addresses can be verified for security purposes.
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleVerificationSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Verification Status
                    </label>
                    <select
                      value={verificationForm.status}
                      onChange={(e) => setVerificationForm({...verificationForm, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="PENDING_MANUAL_VERIFICATION">Pending Verification</option>
                      <option value="VERIFIED">Verified</option>
                      <option value="FAILED">Failed</option>
                      <option value="EXPIRED">Expired</option>
                      <option value="SUSPENDED">Suspended</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Verification Notes
                    </label>
                    <textarea
                      value={verificationForm.notes}
                      onChange={(e) => setVerificationForm({...verificationForm, notes: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add verification notes..."
                    />
                    <p className="text-gray-500 text-xs mt-1">
                      Note: CEA expiry date is now collected during agent registration and cannot be modified here.
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowVerificationModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                    >
                      Update Verification
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}

        {/* Verification Tools Modal */}
        {showVerificationTools && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white"
            >
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    CEA Verification Tools
                  </h3>
                  <button
                    onClick={() => setShowVerificationTools(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                {loadingTools ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading verification tools...</p>
                  </div>
                ) : verificationTools ? (
                  <div className="space-y-4">
                    {/* Agent Info */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-lg mb-3">Agent Information</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium">{verificationTools.agent.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">CEA Number:</span>
                          <span className="font-mono">{verificationTools.agent.ceaNumber || 'Not provided'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Email:</span>
                          <span className="font-medium">{verificationTools.agent.email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status:</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            verificationTools.agent.riskLevel === 'HIGH' ? 'bg-red-100 text-red-800' :
                            verificationTools.agent.riskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {verificationTools.agent.riskLevel} Risk
                          </span>
                        </div>
                      </div>
                      
                      {verificationTools.agent.warnings.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                          <p className="text-sm font-medium text-red-800 mb-1">Issues Found:</p>
                          <ul className="text-sm text-red-700">
                            {verificationTools.agent.warnings.map((warning, index) => (
                              <li key={index}>• {warning}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium text-lg mb-3">Verification Links</h4>
                      <div className="space-y-3">
                        <a
                          href={verificationTools.quickActions.ceaSearch.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full inline-flex items-center justify-center px-4 py-2 border border-blue-600 text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50"
                        >
                          <ExternalLink size={16} className="mr-2" />
                          {verificationTools.quickActions.ceaSearch.description}
                        </a>
                        <a
                          href={verificationTools.quickActions.googleSearch.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full inline-flex items-center justify-center px-4 py-2 border border-green-600 text-sm font-medium rounded-md text-green-600 bg-white hover:bg-green-50"
                        >
                          <ExternalLink size={16} className="mr-2" />
                          {verificationTools.quickActions.googleSearch.description}
                        </a>
                      </div>
                    </div>

                    {/* Simple Checklist */}
                    <div className="bg-white border p-4 rounded-lg">
                      <h4 className="font-medium text-lg mb-3">Verification Steps</h4>
                      <div className="space-y-3">
                        {verificationTools.verificationTools.verificationSteps.map((step) => (
                          <div key={step.step} className="flex items-start space-x-3">
                            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium">
                              {step.step}
                            </div>
                            <div>
                              <h5 className="font-medium text-gray-900">{step.title}</h5>
                              <p className="text-sm text-gray-600">{step.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recommendations */}
                    {verificationTools.verificationTools.recommendations.length > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                        <h4 className="font-medium text-lg mb-3">Action Required</h4>
                        <div className="space-y-2">
                          {verificationTools.verificationTools.recommendations.map((rec, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <AlertTriangle size={16} className="text-yellow-600 mt-0.5" />
                              <div>
                                <p className="font-medium text-yellow-800">{rec.action}</p>
                                <p className="text-sm text-yellow-700">{rec.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Failed to load verification tools</p>
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowVerificationTools(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
        </motion.div>
      </div>
    </div>
  );
};

export default CEAVerificationManagement;